/* eslint-disable class-methods-use-this */
/* eslint-disable no-param-reassign */
/* eslint-disable max-classes-per-file */
import { Dispatch } from 'react'
import { mockRCIDepositsDraft } from '@data/mockRCIDepositsDraft'
import { TMkStep, IMkStep, EMkStepStatus } from '@typescript'
import { ACCOUNT_RCI_DEPOSITS } from '@constants'
import { EMkDepositRCIStepName, IMkAccountInputField, IMkAccountState, IMkAnyAccountField, IMkRCIDepositsDraft, TMkRCIDepositsAction } from '../types'
import * as actions from '../utils/actions'

const initialState: IMkAccountState = {
  rciDepositsDraft: undefined,
  rciDepositsErrors: [],
  isLoading: false,
}

export abstract class IMkAccountService {
  static initialState?(): IMkAccountState
  abstract initRCIDepositsFlow(): () => Promise<void>
  abstract checkStepStatus(step: TMkStep, status: EMkStepStatus): boolean
  abstract findStepIndexByName(stepName: string): number
  abstract getContextProps<T>(): T
  abstract getActiveStep(): TMkStep | undefined
  abstract handleStepComplete(): Promise<void>
  abstract handleStepEdit(step: TMkStep): void
  abstract handleStepInputFieldChange(step: TMkStep, fieldName: string, fieldValue: IMkAnyAccountField['value'], fieldType: string, opts?: { checked: boolean }): Promise<void>
  abstract setRCIDepositsError({ raw, message }: { raw?: Error; message?: string }): void
  abstract clearRCIDepositsDraft(): void
}

class MkAccountService implements IMkAccountService {
  private state: IMkAccountState

  private dispatch: Dispatch<TMkRCIDepositsAction>

  private navigate: (path: string) => void

  constructor(state: IMkAccountState, dispatch: Dispatch<TMkRCIDepositsAction>, navigate: (path: string) => void) {
    this.state = state
    this.dispatch = dispatch
    this.navigate = navigate
  }

  public static initialState(): IMkAccountState {
    const rciDepositsDraft = MkAccountService.pullRCIDepositsDraft()

    return {
      ...initialState,
      rciDepositsDraft: rciDepositsDraft || undefined,
    }
  }

  getContextProps<T>(): T {
    const { rciDepositsDraft } = this.state

    const selectDepositFields = Object.values((rciDepositsDraft?.steps.find(({ name }) => name === 'selectDeposit')?.fieldSet || {}) as Record<string, IMkAnyAccountField>)

    return {
      accountService: this,
      ...this.state,
      selectDepositFields,
      initRCIDepositsFlow: this.initRCIDepositsFlow.bind(this),
      checkStepStatus: this.checkStepStatus.bind(this),
      findStepIndex: this.findStepIndexByName.bind(this),
      getActiveStep: this.getActiveStep.bind(this),
      handleStepComplete: this.handleStepComplete.bind(this),
      handleStepEdit: this.handleStepEdit.bind(this),
      handleStepInputFieldChange: this.handleStepInputFieldChange.bind(this),
      clearRCIDepositsDraft: this.clearRCIDepositsDraft.bind(this),
    } as unknown as T
  }

  static pushRCIDepositsDraft(draft: IMkRCIDepositsDraft): void {
    try {
      localStorage.setItem('rciDepositsDraft', JSON.stringify(draft))
    } catch (error) {
      console.error('Failed to save RCI Deposits Draft into local storage with the following error: ', error)
    }
  }

  private static pullRCIDepositsDraft(): IMkRCIDepositsDraft | undefined {
    try {
      const rciDepositsDraftString = localStorage.getItem('rciDepositsDraft')
      const rciDepositsDraft = rciDepositsDraftString ? JSON.parse(rciDepositsDraftString) : undefined

      if (!rciDepositsDraft || !rciDepositsDraft.steps) {
        return undefined
      }
      return rciDepositsDraft
    } catch (error) {
      console.log('Could not load RCI Deposit Draft from storage')
      return undefined
    }
  }

  // API methods
  private static syncRCIDepositsDraft() {
    // Temporary until we have backend to sync with | return the draft stored in local storage or the initial hardcoded draft
    const rciDepositsDraft = MkAccountService.pullRCIDepositsDraft() || mockRCIDepositsDraft
    return { data: rciDepositsDraft as IMkRCIDepositsDraft }
  }

  public initRCIDepositsFlow(): () => Promise<void> {
    return async (): Promise<void> => {
      try {
        const { data: newRCIDepositsDraft } = MkAccountService.syncRCIDepositsDraft()

        this.dispatch(actions.setRCIDepositsDraft(newRCIDepositsDraft))
        this.navigate(ACCOUNT_RCI_DEPOSITS)
      } catch (error) {
        this.setRCIDepositsError({
          raw: error as Error,
          message: (error as any)?.message || 'Failed to initialize RCI Deposits Flow',
        })
      }
    }
  }

  checkStepStatus(step: TMkStep, status: EMkStepStatus): boolean {
    return [status, status?.toString()].includes(step.status)
  }

  findStepIndexByName(stepName: string): number {
    return this.state?.rciDepositsDraft?.steps.findIndex(({ name }) => name === stepName) || 0
  }

  getActiveStep(): TMkStep | undefined {
    return this.state?.rciDepositsDraft?.steps.find((step) => this.checkStepStatus(step, EMkStepStatus.ACTIVE))
  }

  async handleStepInputFieldChange(step: TMkStep, fieldName: string, fieldValue: IMkAnyAccountField['value'], fieldType: string, opts?: { checked?: boolean }): Promise<void> {
    if (fieldType === 'checkbox' && !opts?.checked) {
      fieldValue = ''
    }

    this.dispatch(actions.updateField(step, fieldName, fieldValue, fieldType) as TMkRCIDepositsAction)
  }

  async handleStepComplete(): Promise<void> {
    const currentRCIDepositsStep = this.getActiveStep()

    if (!currentRCIDepositsStep) {
      throw new Error('No RCI Deposits step found')
    }

    // The code below is temporary until we have the logic for step status on the backend implemented
    if (currentRCIDepositsStep && this.state.rciDepositsDraft?.steps) {
      const completedStep = this.state.rciDepositsDraft.steps.find((step) => step.name === currentRCIDepositsStep.name)

      if (completedStep) {
        let nextActiveStepIndex = 0
        const updatedSteps = this.state.rciDepositsDraft.steps.map((step, index) => {
          if (step.name === completedStep.name) {
            nextActiveStepIndex = index + 1
            return {
              ...step,
              status: EMkStepStatus.COMPLETE,
            }
          }
          if (index === nextActiveStepIndex && step.status !== EMkStepStatus.COMPLETE) {
            return {
              ...step,
              status: EMkStepStatus.ACTIVE,
            }
          }
          return step
        })

        const updatedDraft = { ...this.state.rciDepositsDraft, steps: updatedSteps }

        MkAccountService.pushRCIDepositsDraft(updatedDraft)
        this.dispatch(actions.setRCIDepositsDraft(updatedDraft))

        this.setStepTitleCompleted(completedStep)
      }
    }
  }

  handleStepEdit(step: TMkStep): void {
    const stepIndex = this.findStepIndexByName(step.name)

    if (stepIndex === -1) {
      console.error(`Step ${step.name} not found in RCI Deposits steps`)
      return
    }

    const updatedSteps = this.state?.rciDepositsDraft?.steps.map((currentStep, index) => {
      // Set the status based on the index relative to the target step
      let status
      if (index === stepIndex) {
        status = EMkStepStatus.ACTIVE
        this.resetStepTitleCompleted(currentStep)
      } else if (index < stepIndex) {
        status = EMkStepStatus.COMPLETE
      } else {
        status = EMkStepStatus.INCOMPLETE
      }

      return {
        ...currentStep,
        status,
      }
    }) as TMkStep[]

    // If we have an RCI Deposits draft, update it with the new steps
    if (this.state.rciDepositsDraft) {
      const updatedDraft = {
        ...this.state.rciDepositsDraft,
        steps: updatedSteps,
      }

      this.dispatch(actions.setRCIDepositsDraft(updatedDraft))
    }
  }

  private updateStepCompletedTitleState<T = TMkStep>(step: T, title: string): void {
    this.handleStepInputFieldChange(step as TMkStep, 'titleCompleted', title, 'text')
  }

  private setStepTitleCompletedSelectDeposit(step: IMkStep): void {
    const selectDepositFields = Object.values((step.fieldSet || {}) as Record<string, IMkAnyAccountField>)
    const valuesFromFormState = selectDepositFields.filter((field: IMkAccountInputField) => field.useForSum).map((field) => Number(step.formState[field.name]))
    const labelToUse = valuesFromFormState.reduce((sum, currentValue) => sum + currentValue, 0).toLocaleString()

    this.updateStepCompletedTitleState(step, labelToUse)
  }

  private setStepTitleCompleted(step: TMkStep): void {
    if (!step) return
    switch (step?.name) {
      case EMkDepositRCIStepName.SELECT_DEPOSIT:
        this.setStepTitleCompletedSelectDeposit(step)
        break
      default:
        console.info('[setStepTitleCompleted]', { stepName: step?.name, step })
    }
  }

  private resetStepTitleCompleted(step: TMkStep): void {
    this.updateStepCompletedTitleState(step, step.title)
  }

  setRCIDepositsError({ raw, message }: { raw?: Error; message?: string }): void {
    this.dispatch(
      actions.setError({
        raw,
        message: message || '',
        name: 'RCI Deposits Error',
      }),
    )
  }

  clearRCIDepositsDraft(): void {
    try {
      localStorage.removeItem('rciDepositsDraft')
    } catch (error) {
      console.error('Failed to clear RCI Deposits Draft from local storage with the following error: ', error)
    }
  }
}

export default MkAccountService
